import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { ScrollablePagination } from '@dealstack/frontend/shared/components/Pagination/ScrollablePagination';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	PAGINATION_LIMIT_PER_PAGE,
	PAGINATION_STATE,
} from '@dealstack/frontend/shared/other/react/hooks/usePagination';
import { memo } from 'react';
import styled, { css } from 'styled-components';

interface IContextualMenu {
	className?: string;
	totalPages: number;
	currentPage: number;
	onPageChange: (newPage: number) => unknown;
	onLimitChange: (newTotalNumber: number) => unknown;
	state: PAGINATION_STATE;
	isModalPagination?: boolean;
}

const availableLimitsPerPage = [
	PAGINATION_LIMIT_PER_PAGE.x25,
	PAGINATION_LIMIT_PER_PAGE.x50,
	PAGINATION_LIMIT_PER_PAGE.x100,
];

const ContextualMenu = ({
	className,
	currentPage,
	totalPages,
	onPageChange,
	onLimitChange,
	state,
}: IContextualMenu) => {
	const tCommonUi = useTranslationCommonUi();
	const onChangeAmountPerPage = () => {
		switch (state.limit) {
			case PAGINATION_LIMIT_PER_PAGE.x25: {
				return onLimitChange(PAGINATION_LIMIT_PER_PAGE.x50);
			}
			case PAGINATION_LIMIT_PER_PAGE.x50: {
				return onLimitChange(PAGINATION_LIMIT_PER_PAGE.x100);
			}
			case PAGINATION_LIMIT_PER_PAGE.x100: {
				return onLimitChange(PAGINATION_LIMIT_PER_PAGE.x25);
			}

			default:
				return onLimitChange(PAGINATION_LIMIT_PER_PAGE.x25);
		}
	};
	return (
		<div className={className}>
			<Header disableVariantMapping variant="sans/11/600">
				{tCommonUi('contextual_menu.showing')}
			</Header>
			<Divider />

			<ScrollablePagination
				totalPages={totalPages}
				currentPage={currentPage}
				onPageChange={onPageChange}
				onLimitChange={onLimitChange}
			/>

			<Divider />
			<Header disableVariantMapping variant="sans/09/600">
				{tCommonUi('contextual_menu.showing')}
			</Header>
			<ChangePageSizeButton
				onClick={onChangeAmountPerPage}
				currentLimitPerPage={state.limit}
				availableLimits={availableLimitsPerPage}
			/>
		</div>
	);
};

const Dot = styled.div<IDot>`
	width: 0.25em;
	height: 0.25em;
	border-radius: 100%;
	background-color: ${({ theme }) => theme.v2.color.neutral['300']};

	${({ active }) =>
		active
			? css`
					background-color: ${({ theme }) => theme.v2.color.secondary[800]};
				`
			: ''}
`;

interface IDot {
	active?: boolean;
}

const DotsSignalizer = styled(
	({
		className,
		availableLimits,
		activeLimit,
	}: {
		className?: string;
		availableLimits: number[];
		activeLimit: number;
	}) => {
		return (
			<div className={className}>
				{availableLimits.map((limitId: number) => (
					<Dot active={limitId === activeLimit} key={limitId} />
				))}
			</div>
		);
	}
)`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
`;

const ContentWrapper = styled.div``;

export const ChangePageSizeButton = styled(
	({
		className,
		onClick,
		currentLimitPerPage,
		availableLimits,
	}: {
		className?: string;
		onClick: () => unknown;
		currentLimitPerPage: number;
		availableLimits: number[];
	}) => {
		const tCommonUi = useTranslationCommonUi();
		return (
			<ButtonV2
				className={className}
				type="button"
				variant="ghost"
				buttonTheme="light"
				size="s"
				onClick={onClick}
			>
				<ContentWrapper>
					<Text variant="sans/11/600" disableVariantMapping>
						{currentLimitPerPage} {tCommonUi('contextual_menu.per_page')}
					</Text>
					<DotsSignalizer
						activeLimit={currentLimitPerPage}
						availableLimits={availableLimits}
					/>
				</ContentWrapper>
			</ButtonV2>
		);
	}
)`
	${ContentWrapper} {
		display: flex;
		flex-grow: 1;

		${DotsSignalizer} {
			margin-left: auto;
			padding-left: 1rem;
		}
	}
`;

export const Header = styled(Text)`
	color: ${({ theme }) => theme.v2.color.neutral['700']};
	margin-bottom: 0.25em;
`;
export const Divider = styled.div`
	display: flex;
	margin-bottom: 0.25em;
	width: 100%;
	height: 1px;
	border-radius: 0.5em;
	background-color: ${({ theme }) => theme.v2.color.neutral['300']};
`;

const Memoized = memo(ContextualMenu);
const Styled = styled(Memoized)`
	display: flex;
	flex-direction: column;
	min-width: 5.75em;
	height: fit-content;
	background-color: ${({ theme }) => theme.v2.color.neutral['100']};
	padding: 0.5em;
	border-radius: 0.25em;
	box-shadow: 0 0.125em 0.25em 0 ${({ theme }) => theme.v2.color.neutral['300']};

	${ChangePageSizeButton} {
		flex-grow: 1;
	}

	${ButtonV2} {
		padding: 0;
		background-color: ${({ theme }) => theme.v2.color.neutral['100']};
		&:focus {
			border: none;
		}
	}
`;

export { Styled as ContextualMenu };
